<script setup lang="ts">
import { ref } from 'vue'

import BaseIcon from '@/components/base/BaseIcon.vue'
import BaseRailItem from '@/components/base/BaseRailItem.vue'
import { usePopover } from '@/composables/ui/use-popover.js'
import type { NavSection } from '@/constants/app/navigation.constant.js'

import SectionNavItems from './SectionNavItems.vue'

/**
 * The 92 px icon rail — fixed chrome, and **the same dark surface in both themes**.
 *
 * That invariance is from the boards and is deliberate: the rail is the one element that
 * never moves and never changes, so it anchors the eye while everything to its right
 * swaps. Its tokens (`--rail-*`) therefore have no dark-mode block, and that absence is a
 * decision rather than an omission.
 *
 * ## What a click does depends on whether the section nav is showing
 *
 * Expanded, a click selects the section and goes to its first screen — the nav beside it
 * then lists the rest. Collapsed, that nav is gone, so the click opens a **flyout** with
 * the same list instead. Without it, collapsing the sidebar would make every second-level
 * screen unreachable except by typing a URL.
 */
const props = defineProps<{ sections: readonly NavSection[]; activeId: string; collapsed: boolean }>()
const emit = defineEmits<{ select: [string] }>()

const root = ref<HTMLElement | null>(null)
const peeked = ref<string | null>(null)
const { open, show, hide } = usePopover(root, () => {
  peeked.value = null
})

function onSelect(section: NavSection): void {
  if (!props.collapsed) {
    emit('select', section.id)

    return
  }

  // Collapsed: the same click opens the list it would otherwise have revealed. A second
  // click on the same section closes it, so the rail is a toggle rather than a trap.
  if (open.value && peeked.value === section.id) {
    hide()

    return
  }

  peeked.value = section.id
  show()
}
</script>

<template>
  <nav
    ref="root"
    class="relative hidden w-[var(--rail-width)] shrink-0 flex-col items-stretch gap-0.5 bg-rail py-4 lg:flex"
    aria-label="Sections"
  >
    <p class="mb-4 text-center text-mono text-rail-accent">Z</p>

    <div
      v-for="section in sections"
      :key="section.id"
      class="relative"
    >
      <BaseRailItem
        :active="section.id === activeId"
        :aria-haspopup="collapsed ? 'menu' : undefined"
        :aria-expanded="collapsed ? open && peeked === section.id : undefined"
        @click="onSelect(section)"
      >
        <BaseIcon
          :name="section.icon"
          :size="22"
        />
        <!-- 11px, uppercase, open tracking — the one place tracking opens up. -->
        <span class="max-w-full text-center text-micro leading-tight">{{ section.label }}</span>
      </BaseRailItem>

      <!--
        The flyout, anchored to the item it belongs to. `left-full` rather than a measured
        position: the rail is a fixed-width column, so the browser already knows where its
        edge is, and anything computed would have to be recomputed on every resize.
      -->
      <Transition name="flyout">
        <div
          v-if="collapsed && open && peeked === section.id"
          class="absolute top-0 left-full z-40 ml-1 w-56 rounded-md border border-border-subtle bg-raised p-2 shadow-overlay"
          role="menu"
          :aria-label="section.label"
        >
          <p class="mb-1 px-2 text-micro text-ink-muted">{{ section.label }}</p>
          <SectionNavItems
            :items="section.items"
            @chose="hide()"
          />
        </div>
      </Transition>
    </div>
  </nav>
</template>

<style scoped>
.flyout-enter-active,
.flyout-leave-active {
  transition:
    opacity var(--motion-fast) var(--motion-ease),
    transform var(--motion-fast) var(--motion-ease);
}
.flyout-enter-from,
.flyout-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}
</style>
